import { useQueryClient } from '@tanstack/react-query';
import { AlertCircle } from '@untitledui/icons';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router-dom';

import {
  AUTH_ERROR_MESSAGES,
  type AuthErrorCode,
} from '@/constants/authErrors';
import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { crmQueryKeys } from '@/lib/crm/query/queries';
import { fetchWithDefaults } from '@/lib/http/client';
import { logger } from '@/lib/logger';

import { pathForPendingFlow, URLs, useAuthStatus } from '../../allauth_auth';
import AuthLayout from '../../layouts/AuthLayout';

function getErrorDetails(errorCode: string | null): {
  title: string;
  message: string;
} {
  if (!errorCode) {
    return AUTH_ERROR_MESSAGES.unknown;
  }
  if (errorCode in AUTH_ERROR_MESSAGES) {
    return AUTH_ERROR_MESSAGES[errorCode as AuthErrorCode];
  }
  return AUTH_ERROR_MESSAGES.unknown;
}

export default function ProviderCallback() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const error = params.get('error');
  const [auth, status] = useAuthStatus();
  const [redirectUrl, setRedirectUrl] = useState<string | null>(null);
  const hasProcessed = useRef(false);
  const queryClient = useQueryClient();

  useEffect(() => {
    // Prevent double-processing in React StrictMode
    if (hasProcessed.current) {
      return;
    }
    hasProcessed.current = true;

    // Check for stored return URL after component mounts
    const returnUrl = sessionStorage.getItem('oauth_return_url');
    logger.debug('ProviderCallback - checking sessionStorage', { returnUrl });

    if (!status.isAuthenticated) {
      setRedirectUrl(pathForPendingFlow(auth) || URLs.LOGIN_URL);
      return;
    }

    if (returnUrl) {
      logger.debug('ProviderCallback - using return URL', { returnUrl });

      // Connect-success path: explicitly invalidate Google + integration
      // CRM caches so the dashboard/calendar pages refresh when the user
      // lands back on them. The full-page OAuth reload makes this defensive
      // in most cases, but it keeps the cache state explicit for any in-app
      // observers that retained references across the reload boundary.
      queryClient.invalidateQueries({ queryKey: ['google'] });
      const orgIdMatch = returnUrl.match(/^\/(\d+)\//);
      if (orgIdMatch) {
        const orgId = parseInt(orgIdMatch[1], 10);
        queryClient.invalidateQueries({
          queryKey: crmQueryKeys.objects(orgId, 'ahoy_external_integration'),
        });
      }

      sessionStorage.removeItem('oauth_return_url');
      setRedirectUrl(returnUrl);
      return;
    }

    // Ask the backend whether a one-shot post-OAuth redirect target is
    // queued for this session. Today this carries the paid-invite Stripe
    // Checkout URL stashed by ``apps.orgs.signals.handle_user_signup``;
    // tomorrow it can carry anything else that needs a server-decided
    // post-auth landing. Endpoint pops the value, so this is single-use.
    //
    // External URLs (Stripe) need a full-page navigation so the browser
    // actually leaves the SPA; internal URLs go through the same code path
    // for consistency (window.location.assign handles both fine — the
    // default-dashboard case is rare enough that the marginal reload cost
    // is acceptable).
    void fetchWithDefaults('/users/api/post-oauth-redirect/')
      .then(response => (response.ok ? response.json() : null))
      .then((data: { redirect_url: string | null } | null) => {
        if (data?.redirect_url) {
          logger.debug('ProviderCallback - using backend redirect URL', {
            redirectUrl: data.redirect_url,
          });
          window.location.assign(data.redirect_url);
          return;
        }
        logger.debug('ProviderCallback - no backend redirect, using default', {
          defaultUrl: URLs.LOGIN_REDIRECT_URL,
        });
        setRedirectUrl(URLs.LOGIN_REDIRECT_URL);
      })
      .catch(err => {
        logger.debug('ProviderCallback - post-oauth-redirect fetch failed', {
          err,
        });
        // Fail open: any error falls back to the default dashboard so a
        // backend hiccup doesn't trap the user on a loading screen.
        setRedirectUrl(URLs.LOGIN_REDIRECT_URL);
      });
  }, [status.isAuthenticated, auth, queryClient]);

  if (!error && redirectUrl) {
    return <Navigate to={redirectUrl} />;
  }

  if (error) {
    const { title, message } = getErrorDetails(error);
    // For errors, try to redirect back to where they came from (e.g., settings page)
    // Fall back to login only if not authenticated
    const returnUrl = sessionStorage.getItem('oauth_return_url');
    const backUrl = status.isAuthenticated
      ? returnUrl || URLs.LOGIN_REDIRECT_URL
      : URLs.LOGIN_URL;
    const backLabel = status.isAuthenticated ? 'Go back' : 'Back to sign in';

    // Clean up the stored URL since we're handling the error
    if (returnUrl) {
      sessionStorage.removeItem('oauth_return_url');
    }

    return (
      <AuthLayout title={title}>
        <div className='flex flex-col gap-8'>
          {/* Icon and Header */}
          <div className='flex flex-col items-center gap-4 text-center'>
            <div className='w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center'>
              <AlertCircle className='w-8 h-8 text-red-600 dark:text-red-400' />
            </div>
            <div className='flex flex-col gap-3'>
              <h1 className='text-3xl font-semibold text-fg-brand-primary md:text-4xl'>
                {title}
              </h1>
              <p className='text-base text-gray-600 dark:text-gray-400'>
                {message}
              </p>
            </div>
          </div>

          {/* Action Button */}
          <div className='flex flex-col gap-4'>
            <Link to={backUrl} className='w-full'>
              <Button className='w-full'>{backLabel}</Button>
            </Link>
          </div>
        </div>
      </AuthLayout>
    );
  }

  // Show loading while determining redirect
  return (
    <AuthLayout title='Connecting…'>
      <div className='flex flex-col gap-8 text-center'>
        <div className='flex flex-col items-center gap-4'>
          <LoadingIndicator size='md' />
          <p className='text-base text-gray-600 dark:text-gray-400'>
            Completing sign in…
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
